import { useStore } from '@nuxtjs/composition-api'

declare const window: any

export class CookieConsent {
  private cookieNameMarketing: string = 'cookieConsentMarketingCookies'
  private cookieNameDialog: string = 'cookieConsentDialog'
  private nuxtStore: any

  private readonly countryList: string[] = ['dk', 'de', 'at', 'be', 'es', 'nl']
  private currentCountry: string = ''

  constructor(currentCountry: string, setStore: boolean = true) {
    this.currentCountry = currentCountry

    if (!process.client) return

    if (setStore) {
      this.nuxtStore = useStore()
      this.nuxtStore.commit(
        'cookieConsent/setDialogFlag',
        this.showConsentDialog()
      )

      this.nuxtStore.commit(
        'cookieConsent/setMarketingCookieFlag',
        this.areMarketingCookiesAllow()
      )

      if (this.doNotTrack()) {
        this.rejectMarketingCookies()
      }
    }
  }

  /**
   * Returns true if the current country is part of the country list.
   * That means a cookie consent is necessary for this country.
   */
  isConsentNecessary(): boolean {
    return this.countryList.includes(this.currentCountry)
  }

  /**
   * Return true if the marketing cookies are allowed.
   * False if not.
   */
  areMarketingCookiesAllow(): boolean {
    return (
      !this.isConsentNecessary() ||
      (process.client &&
        !this.doNotTrack() &&
        document.cookie.includes('cookieConsentMarketingCookies=yes'))
    )
  }

  /**
   * Sets a cookie to save the information that marketing cookies are allowed.
   */
  acceptMarketingCookies() {
    if (process.client) {
      this.setCookie(this.cookieNameDialog, 'shown', 100)
      this.nuxtStore.commit('cookieConsent/setDialogFlag', false)

      /**
       * If the doNotTrack flag of the browser is set is it not possible
       * to activate the marketing cookies.
       */
      if (this.doNotTrack()) {
        this.rejectMarketingCookies()
      } else {
        this.setCookie(this.cookieNameMarketing, 'yes', 100)
        this.nuxtStore.commit('cookieConsent/setMarketingCookieFlag', true)
      }
    }
  }

  /**
   *
   */
  rejectMarketingCookies() {
    if (process.client) {
      if (this.doNotTrack()) {
        document.cookie = ''
      } else {
        this.setCookie(this.cookieNameDialog, 'shown', 100)
        this.nuxtStore.commit('cookieConsent/setDialogFlag', false)

        this.setCookie(this.cookieNameMarketing, 'no', 100)
        this.nuxtStore.commit('cookieConsent/setMarketingCookieFlag', false)
      }
    }
  }

  /**
   * Returns true if the cookie consent question dialog should be shown.
   * False if the marketing cookies are accepted or the doNotTrack flag is true.
   */
  showConsentDialog(): boolean {
    if (this.isConsentNecessary() && process.client && !this.doNotTrack()) {
      return !document.cookie.includes('cookieConsentDialog=shown')
    } else {
      return false
    }
  }

  /**
   * Checks the doNotTrack flag fo the browser and returns true if the flag
   * is set. False if the doNotTrack flag isn't set.
   *
   */
  doNotTrack(): boolean {
    if (!process.client) {
      return false
    } else {
      const dnt: string =
        typeof navigator.doNotTrack !== 'undefined'
          ? (navigator.doNotTrack as string)
          : typeof window.doNotTrack !== 'undefined'
          ? (window.doNotTrack as string)
          : ''

      if (parseInt(dnt) === 0 || dnt === 'no') {
        return false
      } else return parseInt(dnt) === 1 || dnt === 'yes'
    }
  }

  /**
   * Sets a cookie with the given name, value and expire days.
   */
  private setCookie(name: string, value: string, expireDays: number) {
    const d = new Date()
    d.setTime(d.getTime() + expireDays * 24 * 60 * 60 * 1000)
    const expires = 'expires=' + d.toUTCString()
    document.cookie = name + '=' + value + ';' + expires + ';path=/'
  }
}
